import React, { useReducer, useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import imgCoordinador from '../../assets/imgCoordinadorIMDITuxtla.webp';

// Reducer para tabs
const tabReducer = (state, action) => {
  switch (action.type) {
    case 'SET_TAB':
      return action.payload;
    default:
      return state;
  }
};

// Estilos fuera del render
const styles = {
  container: {
    mt: 8,
    backgroundColor: '#c1c5c6',
    color: '#20232A',
    borderRadius: '16px',
    overflow: 'hidden',
    py: { xs: 4, sm: 5, md: 8 },
    px: { xs: 2, sm: 4, md: 6 },
    width: '100%',
    mx: 'auto',
  },
  title: {
    fontWeight: 'bold',
    mb: 2,
    fontFamily: 'Roboto, sans-serif',
    letterSpacing: '0.5px',
    lineHeight: '1.2',
    color: '#20232A',
  },
  descriptionText: {
    color: '#4D5C61',
  },
  imgBox: {
    width: '100%',
    maxWidth: { xs: '95%', sm: '500px', md: '550px' },
    position: 'relative',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 6px 15px rgba(0, 0, 0, 0.2)',
    mx: 'auto',
    img: {
      width: '100%',
      display: 'block',
      objectFit: 'cover',
    },
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: 2,
    mb: 4,
  },
  tabButton: (active) => ({
    px: { xs: 2, md: 3 },
    py: { xs: 0.75, md: 1 },
    fontSize: { xs: '0.8rem', md: '0.9rem' },
    fontWeight: 600,
    color: active ? '#fff' : '#20232A',
    backgroundColor: active ? 'rgb(51, 64, 64)' : '#fff',
    borderRadius: '10px',
    border: '1px solid rgb(51, 64, 64)',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: active ? 'rgb(41, 54, 54)' : '#ECEEEE',
      transform: 'scale(1.05)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
    },
  }),
  tabContentBox: (transitioning) => ({
    transition: 'opacity 0.3s ease, transform 0.3s ease',
    opacity: transitioning ? 0 : 1,
    transform: transitioning ? 'translateY(-10px)' : 'translateY(0)',
    minHeight: '510px', // Altura mínima para evitar el salto
    maxWidth: '500px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '16px',
    textAlign: 'center',
    position: 'relative',
  }),  
};

const CoordinadorComponent = () => {
  const [activeTab, dispatch] = useReducer(tabReducer, 'description');
  const [transitioning, setTransitioning] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const handleTabChange = (tab) => {
    if (tab !== activeTab) {
      setTransitioning(true);
      setTimeout(() => {
        dispatch({ type: 'SET_TAB', payload: tab });
        setTransitioning(false);
      }, 300);
    }
  };

  const descriptionContent = [
    'El coordinador de nuestra institución es un profesional altamente calificado con una trayectoria sobresaliente en el área de ultrasonido diagnóstico y educación médica.',
    'Su experiencia académica y profesional lo ha llevado a ser un referente en el uso de tecnología avanzada para el diagnóstico y la formación de nuevos especialistas.',
    'Con una amplia gama de diplomados y certificaciones en técnicas avanzadas como ultrasonido Doppler y ultrasonido intervencionista, nuestro coordinador combina excelencia académica con experiencia práctica.',
  ];

  const curriculumContent = [
    'Maestría en Ultrasonido Diagnóstico (UMA)',
    'Master en Ultrasonido Musculoesquelético y Rehabilitación (Univ. Cardenal Herrera, Esp.)',
    'Master en Tecnología Médica: especialidad de Radiología (Univ. Europea Miguel de Cervantes).',
    'Doctorado en Educación (UMA)',
    'Diplomados en Doppler avanzado y obstétricos',
    'Diplomado en Ultrasonido intervencionista.',
    'Médico encargado del área de ultrasonido de la CMF ISSSTE Tuxtla.',
  ];

  return (
    <Grid container spacing={4} sx={styles.container} justifyContent="center">
      {/* Header */}
      <Grid item xs={12} sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" sx={styles.title}>
          Conoce a Nuestro <span style={{ color: '#718895' }}>Coordinador</span>
        </Typography>
        <Typography variant="body1" sx={styles.descriptionText}>
          Liderando con experiencia y dedicación
        </Typography>
      </Grid>

      {/* Imagen */}
      <Grid item xs={12} md={5}>
        <Box sx={styles.imgBox}>
          <img
            src={imgCoordinador}
            alt="Coordinador"
            style={styles.imgBox.img}
            loading="lazy"
          />
        </Box>
      </Grid>

      {/* Contenido */}
      <Grid
        item
        xs={12}
        md={7}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isSmallScreen ? 'center' : 'flex-start',
          textAlign: isSmallScreen ? 'center' : 'left',
        }}
      >
        {/* Botones */}
        <Box sx={styles.buttonGroup}>
          {['description', 'curriculum'].map((tab) => (
            <Button
              key={tab}
              variant="contained"
              onClick={() => handleTabChange(tab)}
              sx={styles.tabButton(activeTab === tab)}
            >
              {tab === 'description' ? 'Descripción' : 'Currículum'}
            </Button>
          ))}
        </Box>

        {/* Contenido de pestañas */}
        <Box sx={styles.tabContentBox(transitioning)}>
          {activeTab === 'description' ? (
            descriptionContent.map((text, index) => (
              <Typography
                key={index}
                variant="body1"
                sx={{
                  color: '#4D5C61',
                  lineHeight: '1.75',
                  textAlign: 'justify',
                  mb: index < descriptionContent.length - 1 ? 2 : 0,
                }}
              >
                {text}
              </Typography>
            ))
          ) : (
            <Box>
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: 'bold', color: '#20232A' }}
              >
                Experiencia y Logros
              </Typography>
              <List>
                {curriculumContent.map((item, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemText primary={item} sx={{ color: '#4D5C61' }} />
                    </ListItem>
                    {index < curriculumContent.length - 1 && (
                      <Divider sx={{ backgroundColor: '#E5E7E7' }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default React.memo(CoordinadorComponent);
